/**
 * Shared formatting utilities for Transfer Bonds
 * Extracted from RequestDetailPage and PostingDetailPage to reduce duplication
 */

export const formatCurrency = (value) => {
  if (value == null) return '-';
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
  }).format(value);
};

export const formatDate = (value) => {
  if (!value) return '-';
  return new Date(value).toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  });
};

export const formatDateTime = (value) => {
  if (!value) return '-';
  return new Date(value).toLocaleString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
    hour12: true,
  });
};

export const formatTimeRemaining = (expireDate) => {
  if (!expireDate) return '-';
  const now = new Date();
  const expire = new Date(expireDate);
  const diff = expire - now;

  if (diff <= 0) return 'Expired';

  const hours = Math.floor(diff / (1000 * 60 * 60));
  const minutes = Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60));
  const seconds = Math.floor((diff % (1000 * 60)) / 1000);

  if (hours > 24) {
    const days = Math.floor(hours / 24);
    return `${days}d ${hours % 24}h`;
  }
  return `${hours}h ${minutes}m ${seconds}s`;
};

export const calculateTotalFees = (quote) => {
  const execution = quote?.executionFee || 0;
  const travel = quote?.travelFee || 0;
  const other = quote?.otherFee || 0;
  return execution + travel + other;
};

export const formatAddress = (street1, street2, city, state, zip) => {
  const parts = [street1, street2, city, state, zip].filter(Boolean);
  return parts.length > 0 ? parts.join(', ') : '-';
};

// ============================================
// INPUT FORMATTING (visual only, raw value stored)
// ============================================

/**
 * Format a raw digit string as (555) 123-4567
 * Accepts any string, strips non-digits, formats progressively
 */
export const formatPhoneNumber = (value) => {
  if (!value) return '';
  const digits = value.replace(/\D/g, '').slice(0, 10);
  if (digits.length === 0) return '';
  if (digits.length <= 3) return `(${digits}`;
  if (digits.length <= 6) return `(${digits.slice(0, 3)}) ${digits.slice(3)}`;
  return `(${digits.slice(0, 3)}) ${digits.slice(3, 6)}-${digits.slice(6)}`;
};

/**
 * Strip a formatted phone string back to digits only
 */
export const unformatPhone = (value) => {
  if (!value) return '';
  return value.replace(/\D/g, '').slice(0, 10);
};

/**
 * Format a raw digit string as 123-45-6789
 */
export const formatSSN = (value) => {
  if (!value) return '';
  const digits = value.replace(/\D/g, '').slice(0, 9);
  if (digits.length <= 3) return digits;
  if (digits.length <= 5) return `${digits.slice(0, 3)}-${digits.slice(3)}`;
  return `${digits.slice(0, 3)}-${digits.slice(3, 5)}-${digits.slice(5)}`;
};

/**
 * Strip a formatted SSN back to digits only
 */
export const unformatSSN = (value) => {
  if (!value) return '';
  return value.replace(/\D/g, '').slice(0, 9);
};

/**
 * Format a raw number string with commas for display in an input
 * e.g. "5000000" → "5,000,000" or "5000.50" → "5,000.50"
 */
export const formatCurrencyInput = (value) => {
  if (!value && value !== 0) return '';
  const str = value.toString();
  // Split on decimal
  const parts = str.split('.');
  // Add commas to integer part
  parts[0] = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, ',');
  return parts.join('.');
};

/**
 * Strip commas from a formatted currency input value
 */
export const unformatCurrencyInput = (value) => {
  if (!value) return '';
  return value.replace(/,/g, '');
};
